import type { Configuration } from '~/types/configurator'

export default function useConfiguration(
  configuration: Ref<Configuration | null> = ref(null)
) {
  const values = ref<Record<string, Configuration['values'][number]>>({})
  const multiselectableAccessories = ref<
    Record<string, Configuration['values']>
  >({})

  const currentValues = computed(() => {
    return [
      ...Object.values(values.value),
      ...Object.values(multiselectableAccessories.value).flat(),
    ]
  })

  const {
    globalSurfaceChanges,
    activeSurfaceOption,
    chosenParts,
    surfaceCategoryMismatch,
    surfaceOptionsGroups,
    surfaceTypes,
    surfaceMaps,
    setChosenParts,
    updateChosenPartsSurfaces,
    setGlobalSurfaceChange,
  } = useConfigurationSurfaces(
    configuration,
    values,
    multiselectableAccessories,
    currentValues
  )

  const { optionalBaseParts, toggleOptionalBasePart } =
    useConfigurationOptionalBaseParts(
      configuration,
      currentValues,
      values,
      surfaceTypes,
      surfaceMaps,
      surfaceOptionsGroups
    )

  watch(
    () => configuration.value?.values,
    async () => {
      // reset objects
      const newValues = {} as Record<string, Configuration['values'][number]>
      const newMultiselectableAccessories = {} as Record<
        string,
        Configuration['values']
      >

      if (configuration.value) {
        // split values and multiselectable accessories
        for (const value of configuration.value.values) {
          if (
            configuratorUtils.isMultiselectableAccessory(
              value,
              configuration.value.accessoriesOptions
            )
          ) {
            if (
              !Object.prototype.hasOwnProperty.call(
                newMultiselectableAccessories,
                value.partCategoryNid
              )
            ) {
              newMultiselectableAccessories[value.partCategoryNid] = []
            }
            newMultiselectableAccessories[value.partCategoryNid]?.push(value)
          } else {
            newValues[value.partCategoryNid] = value
          }
        }
      }

      values.value = newValues
      multiselectableAccessories.value = newMultiselectableAccessories
    },
    { immediate: true }
  )

  function addNonRemovableAccessory(
    accessoryCategoryNid: number,
    accessoryNid: number,
    currentNonRemovableAccessories: Configuration['values'],
    newNonRemovableAccessories: Configuration['accessoriesOptions'][number]['values']
  ) {
    if (!configuration.value) return

    const { option, accessory } = configuratorUtils.findAccessory(
      configuration.value.accessoriesOptions,
      accessoryCategoryNid,
      accessoryNid
    )

    if (!option || !accessory) return

    const isNewlyAdded =
      newNonRemovableAccessories.findIndex(
        (value) => value.accessoryNid === accessoryNid
      ) !== -1

    if (!isNewlyAdded) {
      newNonRemovableAccessories.push(accessory)
    }
    const isAlreadyAdded =
      currentNonRemovableAccessories.findIndex(
        (value) => value.partNid === accessoryNid
      ) !== -1

    if (!isAlreadyAdded && !isNewlyAdded) {
      const { surfaceOption, accessoryValue } =
        configuratorUtils.convertAccessoryToValue(
          accessory,
          option,
          currentValues.value,
          surfaceTypes.value,
          surfaceMaps.value,
          surfaceOptionsGroups.value
        )
      if (surfaceOption) {
        configuration.value.surfacesOptions.push(surfaceOption)
      }

      // add non removable accessory to the corresponding multiselectable array
      if (
        !Object.prototype.hasOwnProperty.call(
          multiselectableAccessories.value,
          option.partCategoryNid
        )
      ) {
        multiselectableAccessories.value[option.partCategoryNid] = []
      }
      multiselectableAccessories.value[option.partCategoryNid]?.push(
        accessoryValue
      )
    }
  }

  function updateNonRemovableAccessories() {
    // NOTE: only accessories that are in multiselectable option can be non removable
    if (!configuration.value) return

    // get current values with surface
    const valuesWithSurface = currentValues.value.filter(
      (value) => value.surfaceNid !== null && value.surfaceNid !== undefined
    )
    // get all possible non removable accessories
    const allNonRemovableAccessories = configuration.value.accessoriesOptions
      .filter((option) => option.multiselectable)
      .flatMap((option) => option.values)
      .filter((value) => !value.isRemovable)
    // get current non removable accessories that are in the configuration
    const currentNonRemovableAccessories = currentValues.value.filter((value) =>
      allNonRemovableAccessories.find(
        (val) => val.accessoryNid === value.partNid
      )
    )
    // new non removable accessories that should be in the configuration
    const newNonRemovableAccessories: Configuration['accessoriesOptions'][number]['values'] =
      []

    // add missing non removable accessories to the configuration (once)
    valuesWithSurface.forEach((value) => {
      const { collection, surface } = configuratorUtils.findSurfaceByNid(
        surfaceTypes.value,
        surfaceMaps.value,
        value.surfaceNid
      )
      collection?.priceIncrements.forEach((priceIncrement) => {
        if (
          priceIncrement.accessoryCategoryNid &&
          priceIncrement.accessoryNid
        ) {
          addNonRemovableAccessory(
            priceIncrement.accessoryCategoryNid,
            priceIncrement.accessoryNid,
            currentNonRemovableAccessories,
            newNonRemovableAccessories
          )
        }
      })
      surface?.priceIncrements.forEach((priceIncrement) => {
        if (
          priceIncrement.accessoryCategoryNid &&
          priceIncrement.accessoryNid
        ) {
          addNonRemovableAccessory(
            priceIncrement.accessoryCategoryNid,
            priceIncrement.accessoryNid,
            currentNonRemovableAccessories,
            newNonRemovableAccessories
          )
        }
      })
    })

    // find non removable accessories that are not needed anymore
    const notNeededNonRemovableAccessories =
      currentNonRemovableAccessories.reduce(
        (acc, value) => {
          if (
            newNonRemovableAccessories.findIndex(
              (val) => val.accessoryNid === value.partNid
            ) === -1
          ) {
            acc.push(value)
          }
          return acc
        },
        [] as Configuration['values'][number][]
      )

    // remove not needed non removable accessories from the configuration
    for (const value of notNeededNonRemovableAccessories) {
      const { option, accessory } = configuratorUtils.findAccessory(
        configuration.value.accessoriesOptions,
        value.partCategoryNid,
        value.partNid
      )

      if (accessory?.hasSurfacesOption) {
        // remove surface option if accessory has surfaces option
        const index = configuration.value.surfacesOptions.findIndex(
          (value) => value.partCategoryNid === option.partCategoryNid
        )
        if (index !== undefined && index !== -1) {
          configuration.value.surfacesOptions.splice(index, 1)
        }
      }
      if (option) {
        // remove non removable accessory from multiselectable array
        const index = multiselectableAccessories.value[
          option.partCategoryNid
        ]?.findIndex((val) => val.partNid === value.partNid)
        if (index !== undefined && index !== -1) {
          multiselectableAccessories.value[option.partCategoryNid]?.splice(
            index,
            1
          )
        }
      }
    }
  }

  watch(
    () => configuration.value?.surfacesOptions,
    () => {
      updateNonRemovableAccessories()
    },
    { deep: true }
  )

  const { checkAccessoryChange, applyAccessoryChanges } =
    useConfigurationAccessoryChange(
      configuration,
      values,
      multiselectableAccessories,
      currentValues,
      surfaceTypes,
      surfaceMaps,
      surfaceOptionsGroups
    )

  return {
    values,
    multiselectableAccessories,
    currentValues,
    globalSurfaceChanges,
    activeSurfaceOption,
    chosenParts,
    surfaceCategoryMismatch,
    surfaceOptionsGroups,
    surfaceTypes,
    surfaceMaps,
    optionalBaseParts,
    setChosenParts,
    updateChosenPartsSurfaces,
    setGlobalSurfaceChange,
    updateNonRemovableAccessories,
    toggleOptionalBasePart,
    checkAccessoryChange,
    applyAccessoryChanges,
  }
}
